import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const LocalPaymentGatewaySettings = () => {
  const { user, fetchWithRetry, isLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [momoConfig, setMomoConfig] = useState({
    partnerCode: "",
    accessKey: "",
    secretKey: "",
    endpoint: "",
    returnUrl: "",
    notifyUrl: "",
    enabled: false,
  });
  const [zalopayConfig, setZalopayConfig] = useState({
    appId: "",
    key1: "",
    key2: "",
    callbackUrl: "",
    endpoint: "",
    enabled: false,
  });
  const [vnpayConfig, setVnpayConfig] = useState({
    vnpTmnCode: "",
    vnpHashSecret: "",
    vnpUrl: "",
    vnpReturnUrl: "",
    vnpIpnUrl: "",
    enabled: false,
  });
  const [vietqrConfig, setVietqrConfig] = useState({
    bankBin: "",
    accountNumber: "",
    accountName: "",
    apiCheckStatus: "",
    callbackUrl: "",
    enabled: false,
  });

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWithRetry({ method: "get", url: "/api/local-payments/local-payment-gateways" });
      const { momo, zalopay, vnpay, vietqr } = res.data;

      if (momo) {
        setMomoConfig({
          partnerCode: momo.partnerCode || "",
          accessKey: momo.accessKey || "",
          secretKey: momo.secretKey || "",
          endpoint: momo.endpoint || "",
          returnUrl: momo.returnUrl || "",
          notifyUrl: momo.notifyUrl || "",
          enabled: momo.enabled || false,
        });
      }

      if (zalopay) {
        setZalopayConfig({
          appId: zalopay.appId || "",
          key1: zalopay.key1 || "",
          key2: zalopay.key2 || "",
          callbackUrl: zalopay.callbackUrl || "",
          endpoint: zalopay.endpoint || "",
          enabled: zalopay.enabled || false,
        });
      }

      if (vnpay) {
        setVnpayConfig({
          vnpTmnCode: vnpay.vnpTmnCode || "",
          vnpHashSecret: vnpay.vnpHashSecret || "",
          vnpUrl: vnpay.vnpUrl || "",
          vnpReturnUrl: vnpay.vnpReturnUrl || "",
          vnpIpnUrl: vnpay.vnpIpnUrl || "",
          enabled: vnpay.enabled || false,
        });
      }

      if (vietqr) {
        setVietqrConfig({
          bankBin: vietqr.bankBin || "",
          accountNumber: vietqr.accountNumber || "",
          accountName: vietqr.accountName || "",
          apiCheckStatus: vietqr.apiCheckStatus || "",
          callbackUrl: vietqr.callbackUrl || "",
          enabled: vietqr.enabled || false,
        });
      }

      setLoading(false);
    } catch (error) {
      setError("Không thể tải thông tin cấu hình cổng thanh toán nội địa!");
      setLoading(false);
    }
  }, [fetchWithRetry]);

  const handleSaveSettings = async () => {
    try {
      setError(null);
      setSuccessMessage(null);

      // Kiểm tra dữ liệu VietQR
      if (vietqrConfig.enabled && (!vietqrConfig.bankBin || !vietqrConfig.accountNumber)) {
        setError("Cấu hình VietQR không hợp lệ! Cần nhập Mã ngân hàng (Bank BIN) và Số tài khoản ngân hàng.");
        return;
      }

      console.log("Dữ liệu gửi lên API:", { momo: momoConfig, zalopay: zalopayConfig, vnpay: vnpayConfig, vietqr: vietqrConfig });

      const response = await fetchWithRetry({
        method: "post",
        url: "/api/local-payments/local-payment-gateways",
        data: { momo: momoConfig, zalopay: zalopayConfig, vnpay: vnpayConfig, vietqr: vietqrConfig },
      });
      setSuccessMessage(response.data.message || "Lưu cấu hình thành công!");
    } catch (error) {
      setError(error.response?.data?.message || "Không thể lưu cấu hình cổng thanh toán nội địa!");
    }
  };

  useEffect(() => {
    if (isLoading) return;
    if (!user || (user.role !== "admin" && user.role !== "mod")) {
      navigate("/login");
      return;
    }
    loadSettings();
  }, [user, navigate, loadSettings, isLoading]);

  if (loading) return <p className="text-center text-gray-500">Đang tải...</p>;

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Cấu hình cổng thanh toán nội địa</h2>
      {error && <p className="text-red-500 mb-4">{error}</p>}
      {successMessage && <p className="text-green-500 mb-4">{successMessage}</p>}

      {/* MoMo Settings */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Cấu hình MoMo</h3>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Partner Code</label>
            <input
              type="text"
              value={momoConfig.partnerCode}
              onChange={(e) => setMomoConfig({ ...momoConfig, partnerCode: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập Partner Code từ MoMo"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Access Key</label>
            <input
              type="text"
              value={momoConfig.accessKey}
              onChange={(e) => setMomoConfig({ ...momoConfig, accessKey: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập Access Key từ MoMo"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Secret Key</label>
            <input
              type="text"
              value={momoConfig.secretKey}
              onChange={(e) => setMomoConfig({ ...momoConfig, secretKey: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập Secret Key từ MoMo"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Endpoint</label>
            <input
              type="text"
              value={momoConfig.endpoint}
              onChange={(e) => setMomoConfig({ ...momoConfig, endpoint: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập Endpoint API của MoMo (ví dụ: https://test-payment.momo.vn/v2/gateway/api)"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Return URL</label>
            <input
              type="text"
              value={momoConfig.returnUrl}
              onChange={(e) => setMomoConfig({ ...momoConfig, returnUrl: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập URL chuyển hướng sau khi thanh toán"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Notify URL</label>
            <input
              type="text"
              value={momoConfig.notifyUrl}
              onChange={(e) => setMomoConfig({ ...momoConfig, notifyUrl: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập URL để MoMo gửi thông báo (webhook)"
            />
          </div>
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              checked={momoConfig.enabled}
              onChange={(e) => setMomoConfig({ ...momoConfig, enabled: e.target.checked })}
              className="mr-2"
            />
            <label className="text-gray-700 font-medium">Kích hoạt MoMo</label>
          </div>
        </div>
      </div>

      {/* ZaloPay Settings */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Cấu hình ZaloPay</h3>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">App ID</label>
            <input
              type="text"
              value={zalopayConfig.appId}
              onChange={(e) => setZalopayConfig({ ...zalopayConfig, appId: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập App ID từ ZaloPay"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Key1 (MAC Key)</label>
            <input
              type="text"
              value={zalopayConfig.key1}
              onChange={(e) => setZalopayConfig({ ...zalopayConfig, key1: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập Key1 từ ZaloPay (dùng để tạo MAC)"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Key2 (Verify Key)</label>
            <input
              type="text"
              value={zalopayConfig.key2}
              onChange={(e) => setZalopayConfig({ ...zalopayConfig, key2: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập Key2 từ ZaloPay (dùng để xác minh callback)"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Callback URL</label>
            <input
              type="text"
              value={zalopayConfig.callbackUrl}
              onChange={(e) => setZalopayConfig({ ...zalopayConfig, callbackUrl: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập URL để ZaloPay gửi callback (webhook)"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Endpoint</label>
            <input
              type="text"
              value={zalopayConfig.endpoint}
              onChange={(e) => setZalopayConfig({ ...zalopayConfig, endpoint: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập Endpoint API của ZaloPay (ví dụ: https://sb-openapi.zalopay.vn/v2)"
            />
          </div>
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              checked={zalopayConfig.enabled}
              onChange={(e) => setZalopayConfig({ ...zalopayConfig, enabled: e.target.checked })}
              className="mr-2"
            />
            <label className="text-gray-700 font-medium">Kích hoạt ZaloPay</label>
          </div>
        </div>
      </div>

      {/* VNPay Settings */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Cấu hình VNPay</h3>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">vnp_TmnCode</label>
            <input
              type="text"
              value={vnpayConfig.vnpTmnCode}
              onChange={(e) => setVnpayConfig({ ...vnpayConfig, vnpTmnCode: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập vnp_TmnCode từ VNPay"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">vnp_HashSecret</label>
            <input
              type="text"
              value={vnpayConfig.vnpHashSecret}
              onChange={(e) => setVnpayConfig({ ...vnpayConfig, vnpHashSecret: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập vnp_HashSecret từ VNPay"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">vnp_Url</label>
            <input
              type="text"
              value={vnpayConfig.vnpUrl}
              onChange={(e) => setVnpayConfig({ ...vnpayConfig, vnpUrl: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập vnp_Url từ VNPay (ví dụ: https://sandbox.vnpayment.vn/paymentv2/vpcpay.html)"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">vnp_ReturnUrl</label>
            <input
              type="text"
              value={vnpayConfig.vnpReturnUrl}
              onChange={(e) => setVnpayConfig({ ...vnpayConfig, vnpReturnUrl: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập URL chuyển hướng sau khi thanh toán"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">vnp_IpnUrl</label>
            <input
              type="text"
              value={vnpayConfig.vnpIpnUrl}
              onChange={(e) => setVnpayConfig({ ...vnpayConfig, vnpIpnUrl: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập URL để VNPay gửi thông báo (webhook)"
            />
          </div>
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              checked={vnpayConfig.enabled}
              onChange={(e) => setVnpayConfig({ ...vnpayConfig, enabled: e.target.checked })}
              className="mr-2"
            />
            <label className="text-gray-700 font-medium">Kích hoạt VNPay</label>
          </div>
        </div>
      </div>

      {/* VietQR Settings */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Cấu hình VietQR</h3>
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Mã ngân hàng (Bank BIN)</label>
            <input
              type="text"
              value={vietqrConfig.bankBin}
              onChange={(e) => setVietqrConfig({ ...vietqrConfig, bankBin: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập mã ngân hàng (ví dụ: 970436 cho Vietcombank)"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Số tài khoản ngân hàng</label>
            <input
              type="text"
              value={vietqrConfig.accountNumber}
              onChange={(e) => setVietqrConfig({ ...vietqrConfig, accountNumber: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập số tài khoản ngân hàng"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Tên chủ tài khoản</label>
            <input
              type="text"
              value={vietqrConfig.accountName}
              onChange={(e) => setVietqrConfig({ ...vietqrConfig, accountName: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập tên chủ tài khoản (ví dụ: Nguyễn Văn A)"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">API kiểm tra trạng thái giao dịch</label>
            <input
              type="text"
              value={vietqrConfig.apiCheckStatus}
              onChange={(e) => setVietqrConfig({ ...vietqrConfig, apiCheckStatus: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập API kiểm tra trạng thái giao dịch (nếu có)"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Callback URL</label>
            <input
              type="text"
              value={vietqrConfig.callbackUrl}
              onChange={(e) => setVietqrConfig({ ...vietqrConfig, callbackUrl: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nhập URL để nhận callback từ ngân hàng (nếu có)"
            />
          </div>
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              checked={vietqrConfig.enabled}
              onChange={(e) => setVietqrConfig({ ...vietqrConfig, enabled: e.target.checked })}
              className="mr-2"
            />
            <label className="text-gray-700 font-medium">Kích hoạt VietQR</label>
          </div>
        </div>
      </div>

      <button
        onClick={handleSaveSettings}
        className="w-full px-6 py-3 rounded-lg transition duration-200 font-semibold text-lg bg-blue-500 text-white hover:bg-blue-600"
      >
        Lưu cấu hình
      </button>
    </div>
  );
};

export default LocalPaymentGatewaySettings;